import { Capacitor } from "@capacitor/core";
import { isAxiosError } from "axios";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../contexts/AuthProvider";
import { useAnalytics } from "../contexts/analytics/hooks";
import { useSnackbar } from "../contexts/SnackbarContext";
import { backendService } from "../services/backend.service";
import { localStorageService } from "../services/LocalStorageService";
import { readRefreshToken } from "../services/storageService.service";
import { uiVisibleErrorKey } from "../utils/errors";
import { UserView } from "../utils/models";
import { canSwitchBetweenViews, resolveDepartmentIdForView } from "../utils/utilFunctions";

/** The current view (admin/staff) and the ability to switch. Source of truth is userInfo, not the JWT. */
export interface ViewMode {
	/** Undefined while userInfo is loading, or for a pre-view token. Treat as "not known yet". */
	view: UserView | undefined;
	/** True if the user can actually reach both views — admin somewhere AND a nurse somewhere. */
	canSwitchView: boolean;
	/** True while a switch is in flight. */
	isSwitching: boolean;
	/** Move to the other view. Resolves only on failure — success reloads the page. */
	switchView: () => Promise<void>;
}

export const useViewMode = (): ViewMode => {
	const { userInfo, saveTokenData, impersonateUser } = useAuth();
	const { track, trackError } = useAnalytics();
	const { showSnackbar } = useSnackbar();
	const { t } = useTranslation();
	const [isSwitching, setIsSwitching] = useState(false);

	const view = userInfo.view;
	const canSwitchView = canSwitchBetweenViews(userInfo.adminDepartmentIds, userInfo.staffDepartmentIds);
	// Re-impersonating needs the target's identifier AND the support token that authorized it —
	// the ambient token belongs to the impersonated user, who cannot impersonate anyone.
	const impersonatedUsername = userInfo.impersonated_by ? userInfo.username : undefined;
	const supportToken = localStorageService.getOriginalToken() ?? undefined;

	// The department the user is standing in now: the stored choice if still valid, else the first.
	const currentDepartmentId = useMemo(() => {
		const departmentIds = userInfo.departments?.map((department) => department.department_id) ?? [];
		const storedDepartmentId = localStorageService.getChosenDepartmentID();
		return storedDepartmentId && departmentIds.includes(storedDepartmentId) ? storedDepartmentId : departmentIds[0];
	}, [userInfo.departments]);

	const resolveTargetDepartmentId = useCallback(
		(targetView: UserView): string | undefined =>
			resolveDepartmentIdForView(targetView, currentDepartmentId, userInfo.adminDepartmentIds, userInfo.staffDepartmentIds),
		[currentDepartmentId, userInfo.adminDepartmentIds, userInfo.staffDepartmentIds],
	);

	const switchView = useCallback(async () => {
		// view is undefined only before userInfo has loaded.
		if (isSwitching || !view) return;

		const targetView = view === UserView.admin ? UserView.staff : UserView.admin;
		// True once the reload is under way, so the button stays disabled until the page goes.
		let reloading = false;
		setIsSwitching(true);
		track("view_switch_start", { from: view, to: targetView });

		try {
			// Native needs the refresh token explicitly; web sends it via http-only cookie automatically.
			let refreshToken: string | undefined;
			if (Capacitor.isNativePlatform()) {
				const { value, readFailed } = await readRefreshToken();
				if (!value) {
					track("view_switch_error", { from: view, to: targetView, reason: readFailed ? "refresh_token_read_failed" : "refresh_token_absent" });
					showSnackbar(t("error.view.switch.failed"), "error");
					return;
				}
				refreshToken = value;
			}

			const departmentId = resolveTargetDepartmentId(targetView);
			if (!departmentId) {
				// Only reachable if the button rendered on stale data.
				track("view_switch_error", { from: view, to: targetView, reason: "no_target_department" });
				showSnackbar(t("error.view.switch.failed"), "error");
				return;
			}

			// While impersonating, /token/department is wrong: it authenticates off the refresh token,
			// which belongs to the SUPPORT user, so it would end the impersonation. Re-impersonate
			// into the target view instead.
			const targetDepartmentId = departmentId;
			const response =
				impersonatedUsername && supportToken
					? await backendService.impersonateUserInView(impersonatedUsername, targetDepartmentId, targetView, supportToken)
					: await backendService.getDepartmentToken(targetDepartmentId, targetView, refreshToken);

			// The reload drops the token and the app rebuilds one from these two keys, so save both.
			localStorageService.setChosenDepartmentID(targetDepartmentId);
			localStorageService.setChosenView(targetView);
			track("view_switch_success", { from: view, to: targetView, department_id: targetDepartmentId });

			// Reload before installing the token, or the new role is checked against the old URL and flashes /404.
			window.location.replace(`/?ts=${Date.now()}`);
			// The page is on its way out — don't let `finally` re-enable the menu behind it.
			reloading = true;

			if (impersonatedUsername && supportToken) {
				// Impersonation always returns a token; an MFA-shaped response here is a server bug.
				const impersonationToken = response.data.access_token;
				if (!impersonationToken) throw new Error("impersonation response carried no access token");
				await impersonateUser(impersonationToken);
			} else {
				await saveTokenData(response.data);
			}
		} catch (error) {
			trackError("view_switch_error", {
				from: view,
				to: targetView,
				reason: "request_failed",
				error_message: error instanceof Error ? error.message : String(error),
				status: isAxiosError(error) ? error.response?.status : undefined,
			});
			// The backend names which view is not open here (admin or staff), so show its reason.
			// DEFAULT: any other failure — network, or a refusal without a reason — gets the generic message.
			showSnackbar(t(uiVisibleErrorKey(error) ?? "error.view.switch.failed"), "error");
		} finally {
			if (!reloading) setIsSwitching(false);
		}
	}, [isSwitching, view, impersonatedUsername, supportToken, resolveTargetDepartmentId, saveTokenData, impersonateUser, showSnackbar, t, track, trackError]);

	return { view, canSwitchView, isSwitching, switchView };
};
